import React from "react";
import { useHardwareProductsQuery, ProductFragment } from "generated/graphql";
import { LoadingIndicator } from "../LoadingIndicator";
import {
  lookupModelImage,
  unknownImageUrl
} from "utilities/modelImageSelector";
import { useTranslation } from "utilities/i18n";
import { memoize } from "lodash";
import { ImageContainer } from "elements/ImageContainer/ImageContainer";

const parseHardwareModelWords = (
  hardwareModel: string | null | undefined
): Array<string> => {
  if (!hardwareModel) {
    return [];
  }

  return hardwareModel
    .toLowerCase()
    .split(" ")
    .filter(w => !["poly", "series"].includes(w.toLowerCase()));
};

export const parseProductCatalogImages = (data: Array<ProductFragment>) => {
  if (!data) {
    return [];
  }

  return data
    .filter(prod => prod.images.length)
    .map(prod => {
      const words = parseHardwareModelWords(prod.hardwareModel.name);

      return {
        name: prod.hardwareModel.name,
        images: prod.images,
        words
      };
    });
};

/**
 * A "non-store" solution to the problem of running a
 * somewhat expensive operation on every single image being
 * rendered.
 *
 * Memoizes the filtered / mapped images for devices, to prevent
 * the filter / map / etc. from running 100 times on the inventory
 * listing, etc.
 *
 * The second argument (the "resolver" function) is just a way
 * to tell if the filter / mapped function should be run again or not.
 */
const memoizedParseImages = memoize(parseProductCatalogImages, (...args) => {
  return args.length;
});

const useProductImages = () => {
  const { data, fetching } = useHardwareProductsQuery();

  let images;
  if (!fetching && data) {
    images = memoizedParseImages(data.hardwareProducts);
  }

  return { images, fetching };
};

interface ProductImageProps {
  className?: string;
  hardwareModel: string | null;
}

export const useProductImageUrl = (
  hardwareModel: string | null
): { src: string; alt: string; fetching: boolean } => {
  const { t } = useTranslation();
  const { images, fetching } = useProductImages();

  const alt = t("IMAGE_OF", {
    hardwareModel: hardwareModel ?? t("UNKNOWN_MODEL")
  });

  if (fetching || !images) {
    return { src: "", alt, fetching };
  }

  if (!hardwareModel) {
    return { src: unknownImageUrl(), alt, fetching };
  }

  const words = parseHardwareModelWords(hardwareModel);

  const matches = images.filter(product => {
    const matches = product.words.filter(w => words.includes(w));
    return matches.length === product.words.length;
  });

  let match;
  // more matched words is a better overall match
  if (matches.length) {
    matches.sort((a, b) => (a.words.length > b.words.length ? -1 : 1));
    match = matches.length ? matches[0] : undefined;
  }

  // attempt to use image from product catalog FIRST, then use local asset
  let src = match ? match.images[0].url : lookupModelImage(hardwareModel);

  return { src, alt, fetching };
};

/**
 * @deprecated -  Use the <ProductImageInContainer /> component instead
 */
export const ProductImage: React.FC<ProductImageProps> = ({
  hardwareModel,
  className
}) => {
  const { src, alt, fetching } = useProductImageUrl(hardwareModel);
  className = className || "";

  if (fetching) {
    return <LoadingIndicator className="inline pv-1" title=" " />;
  }

  return <img className={className} src={src} alt={alt} />;
};

export const ProductImageInContainer: React.FC<ProductImageProps & {
  maxHeight: number;
  maxWidth?: number;
  className?: string;
}> = ({ hardwareModel, maxWidth, maxHeight, className }) => {
  const { src, alt, fetching } = useProductImageUrl(hardwareModel);
  if (fetching) {
    return <LoadingIndicator className="inline pv-1" title=" " />;
  }

  return (
    <ImageContainer
      src={src}
      alt={alt}
      className={className}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
    />
  );
};
